import {targets, useReferentialContext} from "../../Context";
import React, {SetStateAction, useEffect, useRef, useState} from "react";
import {
    CompetitiveGames,
    CompetitiveRoundSummaries,
    CompetitiveRoundSummariesDeathEventInner,
    CompetitiveTeamRoundSummaries,
    Team
} from "../../reckon-api";
import WindowContainer from "../../components/layout/WindowContainer";
import FilterSet from "../../components/selectors/Filterset";
import SpecificGameSelector from "../../components/selectors/Competitive/SpecificGameSelector";
import {Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, Stack, Tooltip, Typography} from "@mui/material";
import SpecificRoundSelector from "../../components/selectors/Competitive/SpecificRoundSelector";
import {MapRender, positionToMapBounds, SmokeAbilityWithAgent, UtilityAbilityWithAgent, KillWithAgent} from "../../components/Maps/MapRender";
import {ArrowDropDown, CheckBox} from "@mui/icons-material";
import {Circle, Rectangle} from "react-leaflet";
import TimestampInRoundSelect from "../../components/selectors/TimestampRangeSelect";
import SpecificGameSelectorDialog from "../../components/selectors/Competitive/SpecificGameSelectorDialog";
import AgentIcon from "../../components/Icons/AgentIcon";
import TeamIcon from "../../components/Icons/TeamIcon";
import {formatDuration} from "../../utils";
import WeaponIcon from "../../components/Icons/WeaponIcon";
import {NeonCard, NeonCardHeader} from "../../NeonComponents/NeonCards";
import Box from "@mui/material/Box";
import SmallDetailButton from "../../components/Buttons/SmallDetailButton";
import {CreditsIcon} from "../../components/Icons/GameIcons";
import {loss_color, scrollable, win_color, attacker_color, defender_color} from "../../theme";
import AbilityIcon from "../../components/Icons/AbilityIcon";
import {Skeleton} from "@mui/lab";

export default function GameReviewer(props: {target: targets}){
    const [selection, updateSelection] = useState(null as CompetitiveGames | null);
    const context = useReferentialContext();
    const [team, setTeam] = useState(context.selectedTeam as Team|null);

    const [roundSelected, setRoundSelected] = useState(null as CompetitiveTeamRoundSummaries|null);
    useEffect(() => {
        setRoundSelected(null);
    }, [selection]);

    useEffect(() => {
        setTeam(context.selectedTeam);
    }, [context.selectedTeam])

    console.log(selection)
    return <WindowContainer direction={'column'} spacing={3}>
        <SpecificGameSelectorDialog game={selection} setGame={updateSelection} target={props.target} team={team} setTeam={setTeam} />
        <Stack direction={'row'} spacing={1} sx={{height:'90%'}}>
            {selection && team ? <SpecificRoundSelector game={selection} team={team} updateSelection={setRoundSelected} single={true}/> : <Typography>Select a game</Typography>}
            {roundSelected && selection ? <RoundReview round={roundSelected} map_id={selection.map}/> : <></>}
        </Stack>
    </WindowContainer>
}

function RoundReview(props: {round: CompetitiveTeamRoundSummaries, map_id: string}){
    const [render, setRender] = useState(true);
    const [timestamp, setTimestamp] = useState(0 as number | number[]);
    const [playerRoundSummaries, setPlayerRoundSummaries] = useState([] as CompetitiveRoundSummaries[]);
    const ctx = useReferentialContext();
    const [roundDurationSeconds, setRoundDurationSeconds] = useState(0);
    const [roundStartTime, setRoundStartTime] = useState(undefined as undefined|number);
    const [plantTime, setPlantTime] = useState(undefined as undefined|number);
    const [plantLocation, setPlantLocation] = useState(undefined as undefined|{x: number, y: number});
    const [roundSmokes, setRoundSmokes] = useState([] as SmokeAbilityWithAgent[]);
    const [roundUtils, setRoundUtils] = useState([] as UtilityAbilityWithAgent[]);
    const [roundKills, setRoundKills] = useState([] as KillWithAgent[])
    const [map, setMap] = useState(ctx.mapsReferential.filter(m => m.id === props.map_id)[0])

    useEffect(() => {
        setMap(ctx.mapsReferential.filter(m => m.id === props.map_id)[0]);
        ctx.reckonApi.competitiveRoundSummaries.list({
            round_index: props.round.round_index, game: props.round.game, ordering:['team1']}
        ).then(r => {
            setPlayerRoundSummaries(r);
            setRoundDurationSeconds(Math.max(...r.map(summary => summary.player_snapshots?.length)));
            setRender(true);
            const roundStart = r[0].freeze_time_end_timestamp as number;
            setRoundStartTime(roundStart);
            // Set timestamp to 5 seconds before round start (freeze time end)
            setTimestamp(Math.max(0, roundStart - 5));
            const playerWithPlant = r.find(summary => summary.plant_time);
            if(playerWithPlant?.plant_time){
                const rawPlantTime = playerWithPlant.plant_time as number;
                setPlantTime(rawPlantTime + (r[0].freeze_time_end_timestamp as number));
                // Extract plant location
                if(playerWithPlant.plant_location){
                    try {
                        const loc = typeof playerWithPlant.plant_location === 'string' 
                            ? JSON.parse(playerWithPlant.plant_location) 
                            : playerWithPlant.plant_location;
                        if(loc && typeof loc.x === 'number' && typeof loc.y === 'number'){
                            setPlantLocation({x: loc.x, y: loc.y});
                        } else {
                            setPlantLocation(undefined);
                        }
                    } catch(e) {
                        setPlantLocation(undefined);
                    }
                } else {
                    setPlantLocation(undefined);
                }
            }else{
                setPlantTime(undefined);
                setPlantLocation(undefined);
            }
            setRoundSmokes(r.flatMap(summary => 
                (summary.smoke_abilities || []).map(smoke => ({ ...smoke, agent: summary.agent, side: summary.side }))
            ));
            setRoundUtils(r.flatMap(summary => 
                (summary.utility_abilities || []).map(util => ({ ...util, agent: summary.agent, side: summary.side }))
            ));
            // Use death_event (from victim's perspective) - contains all deaths in the round
            // killer_agent is now included in the death event
            // The side we set is the KILLER's side (opposite of victim's side)
            const kills = r.flatMap(summary => 
                (summary.death_event || []).map((death: any) => ({ 
                    ...death, 
                    killer_agent: death.killer_agent || '', // Now available from API
                    side: summary.side === 'Attacker' ? 'Defender' : 'Attacker', // Killer's side is opposite of victim
                }))
            );
            setRoundKills(kills);
        });
        /* 
        ctx.reckonApi.competitiveSmokes.list({
            game:props.round.game,
            additional_filters: {round_index:props.round.round_index},
            ordering: ['timestamp_in_round']
        }).then(r => setRoundSmokes(r));
        ctx.reckonApi.competitiveKills.list({
            game: props.round.game,
            additional_filters: {round_index:props.round.round_index},
            ordering: ['timestamp_in_round'],
            perspective: 'killer'
        }).then(r => setRoundKills(r));
        ctx.reckonApi.competitiveUtils.list(
            {
                game: props.round.game,
                additional_filters: {round_index:props.round.round_index},
                ordering: ['timestamp_in_round'],
            }
        ).then(r => setRoundUtils(r))*/
    }, [props.round]);

    useEffect(() => {
        setRender(false);
    }, [props.round.game]);

    return <Stack direction={'row'} sx={{width:'100%', display:'flex', alignItems:'stretch', justifyContent:'center', height:'100%', overflow:'hidden'}} spacing={1}>
        <Stack direction={'column'} sx={{alignItems:'center', justifyContent:'space-evenly', height:'100%', flexShrink: 0, minWidth: 0}}>
            {/*<TeamIcon team_name={playerRoundSummaries[0]?.team1}/>*/}
            <PlayerStateCard timestamp={timestamp as number} summary={playerRoundSummaries[0]}/>
            <PlayerStateCard timestamp={timestamp as number} summary={playerRoundSummaries[1]}/>
            <PlayerStateCard timestamp={timestamp as number} summary={playerRoundSummaries[2]}/>
            <PlayerStateCard timestamp={timestamp as number} summary={playerRoundSummaries[3]}/>
            <PlayerStateCard timestamp={timestamp as number} summary={playerRoundSummaries[4]}/>
        </Stack>
    <Stack direction={'column'} sx={{flex: 1, display:'flex', alignItems:'center', height:'100%', minWidth: 0, overflow: 'hidden', position: 'relative'}} spacing={1}>
        <TimestampInRoundSelect
            value={timestamp}
            onChange={setTimestamp}
            roundDuration={roundDurationSeconds - 1}
            freeze_time_end={roundStartTime}
            spike_time={plantTime}
            kills={roundKills.map(k => ({
                time_in_round: k.time_in_round,
                killer_agent: k.killer_agent,
                victim_agent: k.victim_agent,
                is_headshot: k.is_headshot,
                is_first_blood: k.is_first_blood,
                side: k.side,
            }))}
            sx={{width:'90%', flexShrink: 0}}/>
        <EventsTimeline smokes={roundSmokes} kills={roundKills} utils={roundUtils} team={props.round.team1} setTimestamp={setTimestamp} currentTimestamp={timestamp as number} plantTime={plantTime}/>
        {/*<Stack direction={'row'}>{playerRoundSummaries.map(prs => <Typography>{`${prs.player} : ${prs.positions ? prs.positions.length : 0}`}</Typography>)}</Stack>*/}
        {render && map?
            <Grid container sx={{width:'100%', display:'flex', alignItems:'center', justifyContent:'center', flex: 1, minHeight: 0, position:'relative', zIndex: 100}}>
                <MapRender id={props.map_id} map={map} smokes={roundSmokes.filter(smoke => {
                    const ts = timestamp as number;
                    const smokeTime = smoke.cast_position?.time_in_round ?? 0;
                    return smoke.landing_point && ts > smokeTime && ts < (smokeTime + smoke.duration)
                })}
                    playerPositions={playerRoundSummaries.map(prs => {
                        return {round: prs, position:prs.positions? prs.positions[timestamp as number +1] : undefined }
                    })}
                    kills={roundKills.filter(kill => {
                        const ts = timestamp as number;
                        return kill.time_in_round < ts
                    })}
                   utils={roundUtils.filter(util => {
                       const ts = timestamp as number;
                       const utilTime = util.cast_position?.time_in_round ?? 0;
                       return util.landing_point && ts > utilTime
                   })}
                   spikePosition={plantLocation && plantTime !== undefined && (timestamp as number) >= plantTime ? plantLocation : undefined}
                />
            </Grid>:
            <Grid
                container
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    minHeight: 0
                }}
            >
                <Skeleton variant="rectangular" width="80%" height="100%" />
            </Grid>}
    </Stack>
        <Stack direction={'column'}  sx={{alignItems:'center', justifyContent:'space-evenly', height:'100%', flexShrink: 0, minWidth: 0}}>
            <PlayerStateCard timestamp={timestamp as number} summary={playerRoundSummaries[5]}/>
            <PlayerStateCard timestamp={timestamp as number} summary={playerRoundSummaries[6]}/>
            <PlayerStateCard timestamp={timestamp as number} summary={playerRoundSummaries[7]}/>
            <PlayerStateCard timestamp={timestamp as number} summary={playerRoundSummaries[8]}/>
            <PlayerStateCard timestamp={timestamp as number} summary={playerRoundSummaries[9]}/>
        </Stack>
        </Stack>
}

function PlayerStateCard(props: { summary: CompetitiveRoundSummaries; timestamp: number; debug?: boolean }) {
    const state = props.summary?.player_snapshots ? props.summary.player_snapshots[props.timestamp] : undefined;
    const [showDebug, setShowDebug] = React.useState(false);

    // Helper to find ability by slot in the abilities array
    const getAbility = (slot: string) => {
        if (!state?.abilities || !Array.isArray(state.abilities)) return null;
        return state.abilities.find((a: any) => a.slot === slot);
    };
    
    // For regular abilities: up if base_charges > 0
    const hasCharges = (slot: string) => {
        const ability = getAbility(slot);
        if (!ability) return false;
        return ability.base_charges > 0;
    };
    
    // For ultimate: up if base_charges === max_charges
    const isUltReady = () => {
        const ability = getAbility('ULTIMATE');
        if (!ability) return false;
        return ability.base_charges === ability.max_charges;
    };

    return state ? (
        <>
            {/* Debug Dialog */}
            <Dialog 
                open={showDebug} 
                onClose={() => setShowDebug(false)}
                maxWidth="md"
                PaperProps={{
                    sx: {
                        backgroundColor: 'rgba(20, 20, 30, 0.95)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    pb: 1,
                }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <AgentIcon agent_name={props.summary.agent} sx={{ width: 32, height: 32 }} />
                        <Typography>{props.summary.player} - Snapshot @ {props.timestamp}s</Typography>
                    </Stack>
                    <IconButton onClick={() => setShowDebug(false)} size="small" sx={{ color: 'gray' }}>
                        ✕
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Box 
                        component="pre" 
                        sx={{ 
                            fontFamily: 'monospace', 
                            fontSize: '11px', 
                            whiteSpace: 'pre-wrap',
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            padding: 2,
                            borderRadius: 1,
                            maxHeight: '500px',
                            overflow: 'auto',
                        }}
                    >
                        {JSON.stringify(state, null, 2)}
                    </Box>
                </DialogContent>
            </Dialog>
        <NeonCard
            sx={{
                maxHeight: '140px',
                minHeight: '140px',
                padding: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                filter: `brightness(${state.alive_state?.health ? 100 : 20}%)`,
                transition: "filter 0.3s ease-out",
                width: '280px',
                maxWidth: '280px',
            }} glow_color={props.summary.side === 'Attacker' ? attacker_color: defender_color}
        >
            <Stack direction="row" spacing={2} alignItems="center">
                {/* Agent Icon & Weapon Icon */}
                <Box sx={{ position: "relative", width: 70, height: 70 }}>
                    <AgentIcon agent_name={props.summary.agent} sx={{ width: 70, height: 70 }} />
                    {state.alive_state?.equipped_item?.name && (
                        <Box sx={{ position: "absolute", bottom: -5, right: -5 }}>
                            <WeaponIcon weapon={state.alive_state.equipped_item.name} width={70} height={35} rotate={true} />
                        </Box>
                    )}
                </Box>

                {/* Player Details */}
                <Stack spacing={0.5} flex={1}>
                    <Typography variant="h6">{props.summary.player}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>
                            <CreditsIcon title="Money" color="ffffff" /> {state.money}
                        </Typography>
                        <Typography>
                            <CreditsIcon title="Loadout" color="0000ff" /> {state.loadout_value}
                        </Typography>
                    </Stack>

                    {/* Health & Armor Bars */}
                    <LinearProgress
                        sx={{
                            height: "7px",
                            backgroundColor: "#111",
                            '& .MuiLinearProgress-bar': { backgroundColor: "#ffffff" },
                        }}
                        variant="determinate"
                        value={state.alive_state?.health || 0}
                    />
                    <LinearProgress
                        sx={{
                            height: "7px",
                            width: '50%',
                            backgroundColor: "#111",
                            '& .MuiLinearProgress-bar': { backgroundColor: "#5B1166" },
                        }}
                        variant="determinate"
                        value={(state.alive_state?.armor || 0) * 2}
                    />

                    {/* Abilities */}
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <AbilityIcon
                            agent_name={props.summary.agent}
                            slot="ABILITY_1"
                            abilityState={getAbility('ABILITY_1')}
                            sx={{
                                filter: `brightness(${hasCharges('ABILITY_1') ? 100 : 20}%)`,
                                transition: "filter 0.3s ease-out",
                            }}
                        />
                        <AbilityIcon
                            agent_name={props.summary.agent}
                            slot="ABILITY_2"
                            abilityState={getAbility('ABILITY_2')}
                            sx={{
                                filter: `brightness(${hasCharges('ABILITY_2') ? 100 : 20}%)`,
                                transition: "filter 0.3s ease-out",
                            }}
                        />
                        <AbilityIcon
                            agent_name={props.summary.agent}
                            slot="GRENADE_ABILITY"
                            abilityState={getAbility('GRENADE_ABILITY')}
                            sx={{
                                filter: `brightness(${hasCharges('GRENADE_ABILITY') ? 100 : 20}%)`,
                                transition: "filter 0.3s ease-out",
                            }}
                        />
                        <AbilityIcon
                            agent_name={props.summary.agent}
                            slot="ULTIMATE"
                            abilityState={getAbility('ULTIMATE')}
                            sx={{
                                filter: `brightness(${isUltReady() ? 100 : 20}%)`,
                                transition: "filter 0.3s ease-out",
                            }}
                        />
                        {/* Debug button - hidden, uncomment to enable
                        <Box 
                            onClick={() => setShowDebug(true)}
                            sx={{ 
                                cursor: 'pointer', 
                                fontSize: '10px', 
                                color: 'gray',
                                opacity: 0.5,
                                '&:hover': { opacity: 1 },
                                ml: 0.5,
                            }}
                            title="Show snapshot JSON"
                        >
                            🔍
                        </Box>
                        */}
                    </Stack>
                </Stack>
            </Stack>
        </NeonCard>
        </>
    ) : (
        // Skeleton Placeholder while loading - Neon styled
        <NeonCard
            sx={{
                maxHeight: '140px',
                minHeight: '140px',
                padding: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: '280px',
                maxWidth: '280px',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-150%',
                    width: '150%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.03) 50%, transparent 100%)',
                    animation: 'shimmer 2s infinite',
                },
                '@keyframes shimmer': {
                    '0%': { left: '-150%' },
                    '100%': { left: '150%' },
                },
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                {/* Skeleton for Agent Icon & Weapon */}
                <Box sx={{ position: "relative", width: 70, height: 70, flexShrink: 0 }}>
                    <Skeleton 
                        variant="circular" 
                        width={70} 
                        height={70} 
                        sx={{ 
                            bgcolor: 'rgba(255,255,255,0.05)',
                            '&::after': {
                                background: 'linear-gradient(90deg, transparent, rgba(120,80,160,0.15), transparent)',
                            }
                        }} 
                        animation="pulse"
                    />
                    <Box sx={{ position: "absolute", bottom: -5, right: -5 }}>
                        <Skeleton 
                            variant="rectangular" 
                            width={50} 
                            height={25} 
                            sx={{ 
                                bgcolor: 'rgba(255,255,255,0.03)',
                                borderRadius: '4px',
                            }} 
                            animation="pulse"
                        />
                    </Box>
                </Box>

                {/* Player Details Skeleton */}
                <Stack spacing={0.5} flex={1}>
                    <Skeleton 
                        variant="text" 
                        width={90} 
                        height={28} 
                        sx={{ 
                            bgcolor: 'rgba(255,255,255,0.08)',
                            borderRadius: '4px',
                        }} 
                        animation="pulse"
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton 
                            variant="text" 
                            width={55} 
                            height={18} 
                            sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} 
                            animation="pulse"
                        />
                        <Skeleton 
                            variant="text" 
                            width={55} 
                            height={18} 
                            sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} 
                            animation="pulse"
                        />
                    </Stack>

                    {/* Health & Armor Bars */}
                    <Skeleton 
                        variant="rectangular" 
                        width="100%" 
                        height={7} 
                        sx={{ 
                            bgcolor: 'rgba(255,255,255,0.06)',
                            borderRadius: '2px',
                        }} 
                        animation="wave"
                    />
                    <Skeleton 
                        variant="rectangular" 
                        width="50%" 
                        height={7} 
                        sx={{ 
                            bgcolor: 'rgba(91,17,102,0.2)',
                            borderRadius: '2px',
                        }} 
                        animation="wave"
                    />

                    {/* Ability Icons */}
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        {[0, 1, 2, 3].map((i) => (
                            <Skeleton 
                                key={i}
                                variant="circular" 
                                width={28} 
                                height={28} 
                                sx={{ 
                                    bgcolor: 'rgba(255,255,255,0.05)',
                                    animationDelay: `${i * 0.1}s`,
                                }} 
                                animation="pulse"
                            />
                        ))}
                    </Stack>
                </Stack>
            </Stack>
        </NeonCard>
    );
}


function EventsTimeline(props: {
    smokes: SmokeAbilityWithAgent[],
    kills: KillWithAgent[],
    utils: UtilityAbilityWithAgent[],
    setTimestamp: React.Dispatch<SetStateAction<number | number[]>>,
    team: string,
    currentTimestamp: number,
    plantTime?: number
}){
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const eventRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleWheel = (event: WheelEvent) => {
            event.preventDefault(); // Prevent vertical scrolling
            container.scrollLeft += event.deltaY; // Scroll horizontally instead
        };

        container.addEventListener("wheel", handleWheel);
        return () => container.removeEventListener("wheel", handleWheel);
    }, []);

    // Helper to get time_in_round from different event types
    const getTimeInRound = (event: any): number => {
        if ('time_in_round' in event) return event.time_in_round ?? 0;
        if ('cast_position' in event && event.cast_position?.time_in_round !== undefined) return event.cast_position.time_in_round;
        return 0;
    };
    
    const events: {type: string, data: any}[] = [
        ...props.smokes.map(smoke => {return {type: 'SMOKE_EVENT', data: smoke}}),
        ...props.kills.map(kill => {return {type: 'KILL_EVENT', data: kill}}),
        ...props.utils.map(util => {return {type: 'UTILITY_EVENT', data: util}}),
        ...(props.plantTime !== undefined ? [{type: 'SPIKE_PLANT_EVENT', data: {time_in_round: props.plantTime}}] : []),
    ];
    events.sort((a, b) => getTimeInRound(a.data) - getTimeInRound(b.data));

    // Find the event closest to current timestamp and scroll to it
    useEffect(() => {
        if (events.length === 0 || !scrollContainerRef.current) return;

        const container = scrollContainerRef.current;
        const firstEventTime = Math.floor(getTimeInRound(events[0].data));
        
        // If timestamp is before the first event, scroll to start and don't highlight anything
        if (props.currentTimestamp < firstEventTime) {
            container.scrollTo({
                left: 0,
                behavior: 'smooth'
            });
            return;
        }

        // Find the index of the most recent event that has occurred
        let closestIndex = -1;
        
        for (let idx = events.length - 1; idx >= 0; idx--) {
            const eventTime = Math.floor(getTimeInRound(events[idx].data));
            if (eventTime <= props.currentTimestamp) {
                closestIndex = idx;
                break;
            }
        }

        // Only scroll if we found a matching event
        if (closestIndex >= 0) {
            const eventElement = eventRefs.current[closestIndex];
            if (eventElement) {
                const containerWidth = container.offsetWidth;
                const elementLeft = eventElement.offsetLeft;
                const elementWidth = eventElement.offsetWidth;
                
                // Calculate scroll position to center the element
                const scrollTo = elementLeft - (containerWidth / 2) + (elementWidth / 2);
                
                container.scrollTo({
                    left: Math.max(0, scrollTo),
                    behavior: 'smooth'
                });
            }
        }
    }, [props.currentTimestamp, events.length]);


    // Check if we're before any events
    const firstEventTime = events.length > 0 ? Math.floor(getTimeInRound(events[0].data)) : 0;
    const isBeforeAllEvents = props.currentTimestamp < firstEventTime;

    return <Stack direction={'row'} spacing={1} sx={{...scrollable, width:'100%', minWidth: 0, height: '130px', flexShrink: 0, overflowX: 'auto', overflowY: 'hidden'}} ref={scrollContainerRef}>
        {
            events.map((event, index) => {
                const eventTime = Math.floor(getTimeInRound(event.data));
                // Don't highlight anything if we're before all events
                const isActive = !isBeforeAllEvents && eventTime <= props.currentTimestamp && 
                    (index === events.length - 1 || Math.floor(getTimeInRound(events[index + 1].data)) > props.currentTimestamp);
                
                switch(event.type){
                    case 'SMOKE_EVENT':
                        return <Box key={`smoke-${index}`} ref={(el: HTMLDivElement | null) => eventRefs.current[index] = el}>
                            <SmokeEventCard event={event.data as SmokeAbilityWithAgent} setTimestamp={props.setTimestamp} team={props.team} isActive={isActive}/>
                        </Box>
                    case 'KILL_EVENT':
                        return <Box key={`kill-${index}`} ref={(el: HTMLDivElement | null) => eventRefs.current[index] = el}>
                            <KillEventCard event={event.data as KillWithAgent} setTimestamp={props.setTimestamp} team={props.team} isActive={isActive}/>
                        </Box>
                    case 'UTILITY_EVENT':
                        return <Box key={`util-${index}`} ref={(el: HTMLDivElement | null) => eventRefs.current[index] = el}>
                            <UtilityEventCard event={event.data as UtilityAbilityWithAgent} setTimestamp={props.setTimestamp} team={props.team} isActive={isActive}/>
                        </Box>
                    case 'SPIKE_PLANT_EVENT':
                        return <Box key={`spike-${index}`} ref={(el: HTMLDivElement | null) => eventRefs.current[index] = el}>
                            <SpikePlantEventCard event={event.data as {time_in_round: number}} setTimestamp={props.setTimestamp} isActive={isActive}/>
                        </Box>
                    default:
                        return null;
                }
            })
        }
    </Stack>
}

const SMOKE_COLOR = "#5C6B7A";  // Smoky gray-blue
const UTILITY_COLOR = "#D97706"; // Orange for utility
const KILL_COLOR = "#DC2626";    // Red for kills
const SPIKE_COLOR = "#22C55E";   // Green for spike plant

function SpikePlantEventCard(props: { event: {time_in_round: number}; setTimestamp: React.Dispatch<SetStateAction<number | number[]>>; isActive?: boolean }) {
    const timeInRound = props.event.time_in_round;
    return (
        <Tooltip title="Spike Planted" arrow>
            <Box sx={{ display: "inline-block" }}>
                <NeonCard 
                    glow_color={SPIKE_COLOR}
                    sx={{
                        height: "120px",
                        width: "70px",
                        minWidth: "70px",
                        cursor: "pointer",
                        padding: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                        position: "relative",
                        borderLeft: `3px solid ${attacker_color}`,
                        transform: props.isActive ? 'scale(1.02)' : 'scale(1)',
                        boxShadow: props.isActive ? `0 0 10px ${SPIKE_COLOR}` : 'none',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        background: `radial-gradient(ellipse at center, rgba(34, 197, 94, 0.2) 0%, transparent 70%)`,
                    }}
                    zoom_on_hover
                    onClick={() => props.setTimestamp(Math.floor(timeInRound))}
                >
                    {/* Event type label */}
                    <Box sx={{
                        position: "absolute",
                        top: 3,
                        width: "100%",
                        display: "flex",
                        justifyContent: "center"
                    }}>
                        <Typography sx={{
                            fontSize: "8px",
                            fontWeight: "bold",
                            color: SPIKE_COLOR,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            textShadow: `0 0 8px ${SPIKE_COLOR}`
                        }}>
                            SPIKE
                        </Typography>
                    </Box>

                    {/* Side indicator */}
                    <Box sx={{
                        position: "absolute",
                        top: 12,
                        width: "100%",
                        display: "flex",
                        justifyContent: "center"
                    }}>
                        <Typography sx={{
                            fontSize: "7px",
                            fontWeight: "bold",
                            color: attacker_color,
                            textTransform: "uppercase",
                        }}>
                            ATK
                        </Typography>
                    </Box>

                    {/* Spike Icon */}
                    <Stack direction="column" sx={{ alignItems: "center", flexGrow: 1, justifyContent: "center", mt: 2 }}>
                        <Box sx={{
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '28px',
                        }}>
                            💣
                        </Box>
                    </Stack>

                    {/* Fixed Timestamp Position */}
                    <Box sx={{ position: "absolute", bottom: 3, width: "100%" }}>
                        <Typography sx={{ textAlign: "center", fontSize: "11px", fontWeight: 600 }}>
                            {formatDuration(timeInRound)}
                        </Typography>
                    </Box>
                </NeonCard>
            </Box>
        </Tooltip>
    );
}

function UtilityEventCard (props: {event: UtilityAbilityWithAgent; setTimestamp: React.Dispatch<SetStateAction<number | number[]>> ; team: string; isActive?: boolean}) {
    const timeInRound = props.event.cast_position?.time_in_round ?? 0;
    const sideColor = props.event.side === 'Attacker' ? attacker_color : defender_color;
    return (<Tooltip title={`Utility by ${props.event.agent} (${props.event.side})`} arrow>
            <Box sx={{ display: "inline-block" }}>
        <NeonCard glow_color={UTILITY_COLOR}
            sx={{
                height: "120px",
                width: "70px",
                minWidth: "70px",
                cursor: "pointer",
                padding: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                borderLeft: `3px solid ${sideColor}`,
                transform: props.isActive ? 'scale(1.02)' : 'scale(1)',
                boxShadow: props.isActive ? `0 0 10px ${UTILITY_COLOR}` : 'none',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            zoom_on_hover
            onClick={() => props.setTimestamp(Math.floor(timeInRound))}
        >
            {/* Event type label */}
            <Box sx={{
                position: "absolute",
                top: 3,
                width: "100%",
                display: "flex",
                justifyContent: "center"
            }}>
                <Typography sx={{
                    fontSize: "8px",
                    fontWeight: "bold",
                    color: UTILITY_COLOR,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                }}>
                    UTIL
                </Typography>
            </Box>

            {/* Side indicator */}
            <Box sx={{
                position: "absolute",
                top: 12,
                width: "100%",
                display: "flex",
                justifyContent: "center"
            }}>
                <Typography sx={{
                    fontSize: "7px",
                    fontWeight: "bold",
                    color: sideColor,
                    textTransform: "uppercase",
                }}>
                    {props.event.side === 'Attacker' ? 'ATK' : 'DEF'}
                </Typography>
            </Box>

            <Stack direction="column" sx={{alignItems: "center", flexGrow: 1, justifyContent: "center", mt: 2}}>
                <AbilityIcon agent_name={props.event.agent} slot={props.event.slot} sx={{width: 28, height: 28}}/>
            </Stack>

            {/* Fixed Timestamp Position */}
            <Box sx={{position: "absolute", bottom: 3, width: "100%"}}>
                <Typography sx={{textAlign: "center", fontSize: "11px", fontWeight: 600}}>
                    {formatDuration(timeInRound)}
                </Typography>
            </Box>
        </NeonCard>
            </Box>
        </Tooltip>
    );
}

function SmokeEventCard(props: { event: SmokeAbilityWithAgent; setTimestamp: React.Dispatch<SetStateAction<number | number[]>> ; team: string; isActive?: boolean}) {
    const timeInRound = props.event.cast_position?.time_in_round ?? 0;
    const sideColor = props.event.side === 'Attacker' ? attacker_color : defender_color;
    return (<Tooltip title={`Smoke by ${props.event.agent} (${props.event.side}) - ${Math.round(props.event.duration)}s duration`} arrow>
            <Box sx={{ display: "inline-block" }}>
        <NeonCard glow_color={SMOKE_COLOR}
            sx={{
                height: "120px",
                width: "70px",
                minWidth: "70px",
                cursor: "pointer",
                padding: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                background: `radial-gradient(ellipse at center, rgba(92, 107, 122, 0.3) 0%, transparent 70%)`,
                borderLeft: `3px solid ${sideColor}`,
                transform: props.isActive ? 'scale(1.02)' : 'scale(1)',
                boxShadow: props.isActive ? `0 0 10px ${SMOKE_COLOR}` : 'none',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            zoom_on_hover
            onClick={() => props.setTimestamp(Math.floor(timeInRound))}
        >
            {/* Event type label */}
            <Box sx={{
                position: "absolute",
                top: 3,
                width: "100%",
                display: "flex",
                justifyContent: "center"
            }}>
                <Typography sx={{
                    fontSize: "8px",
                    fontWeight: "bold",
                    color: SMOKE_COLOR,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textShadow: `0 0 8px ${SMOKE_COLOR}`
                }}>
                    SMOKE
                </Typography>
            </Box>

            {/* Side indicator */}
            <Box sx={{
                position: "absolute",
                top: 12,
                width: "100%",
                display: "flex",
                justifyContent: "center"
            }}>
                <Typography sx={{
                    fontSize: "7px",
                    fontWeight: "bold",
                    color: sideColor,
                    textTransform: "uppercase",
                }}>
                    {props.event.side === 'Attacker' ? 'ATK' : 'DEF'}
                </Typography>
            </Box>

            <Stack direction="column" sx={{alignItems: "center", flexGrow: 1, justifyContent: "center", mt: 2, gap: 0}}>
                <Stack direction="row" sx={{alignItems: "center"}} spacing={0.5}>
                    <AgentIcon agent_name={props.event.agent} sx={{width: 14, height: 14}}/>
                    <AbilityIcon agent_name={props.event.agent} slot={props.event.slot} sx={{width: 24, height: 24}}/>
                </Stack>
                {/* Smoke duration indicator */}
                <Typography sx={{fontSize: "9px", color: SMOKE_COLOR}}>
                    {Math.round(props.event.duration)}s
                </Typography>
            </Stack>

            {/* Fixed Timestamp Position */}
            <Box sx={{position: "absolute", bottom: 3, width: "100%"}}>
                <Typography sx={{textAlign: "center", fontSize: "11px", fontWeight: 600}}>
                    {formatDuration(timeInRound)}
                </Typography>
            </Box>
        </NeonCard>
            </Box>
        </Tooltip>
    );
}


function KillEventCard(props: { event: KillWithAgent; setTimestamp: React.Dispatch<SetStateAction<number | number[]>>; team: string; isActive?: boolean }) {
    const sideColor = props.event.side === 'Attacker' ? attacker_color : defender_color;
    return (<Tooltip title={`${props.event.killer_agent} killed ${props.event.victim_agent}${props.event.is_headshot ? ' (headshot)' : ''}`}>
        <Box sx={{ display: "inline-block" }}>
        <NeonCard glow_color={KILL_COLOR}
            sx={{
                height: "120px",
                width: "70px",
                minWidth: "70px",
                cursor: "pointer",
                padding: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                borderLeft: `3px solid ${sideColor}`,
                transform: props.isActive ? 'scale(1.02)' : 'scale(1)',
                boxShadow: props.isActive ? `0 0 10px ${KILL_COLOR}` : 'none',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            zoom_on_hover
            onClick={() => props.setTimestamp(Math.floor(props.event.time_in_round))}
        >
            {/* Event type label */}
            <Box sx={{
                position: "absolute",
                top: 3,
                width: "100%",
                display: "flex",
                justifyContent: "center"
            }}>
                <Typography sx={{
                    fontSize: "8px",
                    fontWeight: "bold",
                    color: KILL_COLOR,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                }}>
                    {props.event.is_first_blood ? "1ST BLOOD" : "KILL"}
                </Typography>
            </Box>

            {/* Side indicator */}
            <Box sx={{
                position: "absolute",
                top: 12,
                width: "100%",
                display: "flex",
                justifyContent: "center"
            }}>
                <Typography sx={{
                    fontSize: "7px",
                    fontWeight: "bold",
                    color: sideColor,
                    textTransform: "uppercase",
                }}>
                    {props.event.side === 'Attacker' ? 'ATK' : 'DEF'}
                </Typography>
            </Box>

            <Stack direction="column" sx={{ alignItems: "center", flexGrow: 1, justifyContent: "center", mt: 2, gap: 0 }}>
                {/* Killer agent icon */}
                <AgentIcon agent_name={props.event.killer_agent} sx={{ width: 20, height: 20 }} />
                {/* Weapon/ability used */}
                {props.event.killer_ability ? (
                    <AbilityIcon agent_name={props.event.killer_agent} slot={props.event.killer_ability} sx={{ width: 16, height: 16 }} />
                ) : props.event.killer_weapon ? (
                    <WeaponIcon weapon={props.event.killer_weapon} width={30} height={14} reverse={true} />
                ) : null}
                {/* Victim agent icon (grayscale = dead) */}
                <AgentIcon agent_name={props.event.victim_agent} sx={{ width: 20, height: 20, filter: "grayscale(100%)", opacity: 0.7 }} />
            </Stack>

            {/* Fixed Timestamp Position */}
            <Box sx={{ position: "absolute", bottom: 3, width: "100%" }}>
                <Typography sx={{ textAlign: "center", fontSize: "11px", fontWeight: 600 }}>
                    {formatDuration(props.event.time_in_round)}
                </Typography>
            </Box>
        </NeonCard>
        </Box>
        </Tooltip>
    );
}

